function formatDate(dateValue, locale = "it-IT") {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  return Number.isNaN(d.getTime()) ? dateValue : d.toLocaleDateString(locale);
}

function formatCurrency(value, locale = "it-IT") {
  return Number(value || 0).toLocaleString(locale, {
    style: "currency",
    currency: "EUR",
  });
}

function formatHour(hour) {
  if (!hour) return "-";
  if (/^\d{1,2}:\d{2}$/.test(hour)) {
    return hour
      .split(":")
      .map((part) => part.padStart(2, "0"))
      .join(":");
  }
  return hour;
}

function ensureSpace(doc, y, requiredHeight) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + requiredHeight > pageHeight - 50) {
    doc.addPage();
    return 45;
  }
  return y;
}

async function fetchAsDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Logo fetch failed: ${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(blob);
  });
}

export async function generateAgencyQuotePDF(form, price, LOGO_URL, PRIMARY = "#1f3763", i18n = {}) {
  const locale = i18n.locale || "it-IT";
  const language = i18n.language || "it";
  const common = i18n.common || {};
  const pdfCopy = {
    it: {
      detailsTitle: "Dettagli Matrimonio",
      date: "Data matrimonio",
      location: "Indirizzo location",
      insuredAmount: "Importo assicurato",
      indemnity: "Indennizzo",
      rainThreshold: "Quantità minima di pioggia",
      coverageTime: "Fascia oraria assicurata",
      premium: "Premio",
      activationTitle: "Come si attiva la polizza?",
      activationSteps: [
        "Compila il form con i dati dell’intestatario della polizza (Sposa o Sposo)",
        "Clicca “Invia la richiesta in agenzia” per avere la quotazione ufficiale per mail",
        "Entro qualche giorno riceverai la proposta di polizza, confermala e inviaci la copia di un tuo documento d’identità (fronte e retro) in corso di validità;",
        "Ti invieremo i documenti da firmare, basta un semplice smartphone",
        "La tua polizza è attiva!",
      ],
      practicalTitle: "Come funziona in pratica?",
      practicalLines: [
        `Il vostro matrimonio sarà il ${formatDate(form.start_date, locale)} a ${form.location || "[location]"}.`,
        "La pioggia verrà misurata, ora per ora, in modo oggettivo da un ente terzo ed indipendente, Radarmeteo.",
        `Se tra le ore ${formatHour(form.start_hour)} e le ore ${formatHour(form.end_hour)} cadranno come minimo ${form.amount_rain || "5mm"} di pioggia, avrai diritto in automatico all’indennizzo di ${formatCurrency(Number(form.amount || 0) * 0.7, locale)}.`,
        "Ecco degli esempi:",
        "Indennizzo sì: 5 mm OK - 5.3 mm OK - 6 mm OK etc",
        "Indennizzo no: 0 mm NO - 3.2 mm NO - 4.9 mm NO",
        "Dopo qualche giorno dall’evento, riceverai un report dettagliato con i mm di pioggia registrati ogni ora.",
        "Ha piovuto abbastanza? Ti verrà solo chiesto l’IBAN e riceverai quanto ti spetta entro 5/10 giorni lavorativi.",
      ],
      footer:
        "Il presente documento non costituisce Proposta contrattuale e non impegna la Compagnia ne l'assicurato ed ha finalita esclusivamente informative e riepilogative.",
      fileName: "preventivo-wedly",
    },
    en: {
      detailsTitle: "Wedding Details",
      date: "Wedding date",
      location: "Venue address",
      insuredAmount: "Insured amount",
      indemnity: "Payout",
      rainThreshold: "Minimum rainfall amount",
      coverageTime: "Insured time slot",
      premium: "Premium",
      activationTitle: "How is the policy activated?",
      activationSteps: [
        "Fill in the form with the policyholder’s details (bride or groom)",
        "Click “Send request to the agency” to receive the official quote by email",
        "Within a few days you will receive the policy proposal. Confirm it and send us a copy of a valid identity document (front and back);",
        "We will send you the documents to sign. A smartphone is enough",
        "Your policy is active!",
      ],
      practicalTitle: "How does it work in practice?",
      practicalLines: [
        `Your wedding will be on ${formatDate(form.start_date, locale)} at ${form.location || "[location]"}.`,
        "Rainfall will be measured hour by hour, objectively, by Radarmeteo, an independent third party.",
        `If between ${formatHour(form.start_hour)} and ${formatHour(form.end_hour)} at least ${form.amount_rain || "5mm"} of rain falls, you will automatically be entitled to a payout of ${formatCurrency(Number(form.amount || 0) * 0.7, locale)}.`,
        "Here are some examples:",
        "Payout yes: 5 mm OK - 5.3 mm OK - 6 mm OK etc",
        "Payout no: 0 mm NO - 3.2 mm NO - 4.9 mm NO",
        "A few days after the event, you will receive a detailed report with the millimetres of rain recorded each hour.",
        "Did it rain enough? You will only be asked for your IBAN and will receive what you are owed within 5/10 working days.",
      ],
      footer:
        "This document is not a contractual proposal and does not bind the Company or the insured party. It is for informational and summary purposes only.",
      fileName: "wedly-quote",
    },
  }[language] || {};

  if (!window.jspdf?.jsPDF) {
    alert(common.pdfNotReady || "Libreria PDF non ancora pronta. Riprova tra qualche secondo.");
    return;
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(PRIMARY);
  doc.rect(0, 0, pageWidth, 70, "F");

  const logoCandidates = [
    LOGO_URL,
    `${process.env.PUBLIC_URL || ""}/wedly-logo.png`,
    "https://wedly.it/wp-content/uploads/2025/01/logo-wedly_1@4x-1024x348.png",
  ].filter(Boolean);

  let logoDataUrl = null;
  for (const candidate of logoCandidates) {
    try {
      // eslint-disable-next-line no-await-in-loop
      logoDataUrl = await fetchAsDataUrl(candidate);
      if (logoDataUrl) break;
    } catch {
      // Try next source.
    }
  }

  if (!logoDataUrl) {
    alert(common.logoMissing || "Logo obbligatorio non disponibile: impossibile generare il PDF.");
    return;
  }

  const logoWidth = 148;
  const logoHeight = 42;
  const logoX = pageWidth - logoWidth - 24;
  doc.addImage(logoDataUrl, "PNG", logoX, 14, logoWidth, logoHeight, undefined, "FAST");

  let y = 104;
  const bodyLineHeight = 18;
  const titleTopMargin = 8;

  const tableRows = [
    [pdfCopy.date, formatDate(form.start_date, locale)],
    [pdfCopy.location, form.location || "-"],
    [pdfCopy.insuredAmount, formatCurrency(form.amount, locale)],
    [pdfCopy.indemnity, formatCurrency(Number(form.amount || 0) * 0.7, locale)],
    [pdfCopy.rainThreshold, form.amount_rain || "5mm"],
    [pdfCopy.coverageTime, `${formatHour(form.start_hour)} - ${formatHour(form.end_hour)}`],
    [pdfCopy.premium, price != null ? formatCurrency(price, locale) : "-"],
  ];

  y += titleTopMargin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor("#1f3864");
  doc.text(pdfCopy.detailsTitle, 24, y);
  y += 14;

  doc.autoTable({
    startY: y,
    body: tableRows,
    theme: "grid",
    margin: { left: 24, right: 24 },
    styles: {
      font: "helvetica",
      fontSize: 11,
      cellPadding: 7,
      textColor: [30, 30, 30],
      lineColor: [207, 217, 230],
      lineWidth: 0.6,
    },
    columnStyles: {
      0: { cellWidth: 180, fontStyle: "bold", fillColor: [248, 251, 255] },
      1: { cellWidth: "auto" },
    },
  });

  y = doc.lastAutoTable.finalY + 34;

  y = ensureSpace(doc, y, 210);

  y += titleTopMargin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor("#1f3864");
  doc.text(pdfCopy.activationTitle, 24, y);
  y += 18;

  pdfCopy.activationSteps.forEach((step, index) => {
    y = ensureSpace(doc, y, bodyLineHeight + 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor("#00b0f0");
    doc.text(`${index + 1})`, 28, y);

    doc.setFont("helvetica", "normal");
    doc.setTextColor("#202020");
    const wrapped = doc.splitTextToSize(step, 510);
    doc.text(wrapped, 50, y, { lineHeightFactor: 1.45 });
    y += wrapped.length * bodyLineHeight + 2;
  });

  y += 14;
  y = ensureSpace(doc, y, 150);

  y += titleTopMargin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor("#1f3864");
  doc.text(pdfCopy.practicalTitle, 24, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor("#202020");

  pdfCopy.practicalLines.forEach((line) => {
    y = ensureSpace(doc, y, bodyLineHeight + 12);
    const wrapped = doc.splitTextToSize(line, 540);
    doc.text(wrapped, 24, y, { lineHeightFactor: 1.45 });
    y += wrapped.length * bodyLineHeight + 4;
  });

  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.8);
  doc.setTextColor("#777777");
  const footerWrapped = doc.splitTextToSize(pdfCopy.footer, 540);
  doc.text(footerWrapped, 24, pageHeight - 24);

  doc.save(`${pdfCopy.fileName}-${form.start_date || "evento"}.pdf`);
}
